import {Component, NgZone, OnDestroy} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ConnectionService } from '@services/connection-service';
import { LocalStorageService } from '@services/local-storage-service';
import { EventLoggerService } from '@services/event-logger-service';
import { BroadcastService } from '@services/broadcast-service';
import { interval, Subscription } from 'rxjs';
import { AppConfig } from '../../app.config';
import { AppWebBridgeService } from '@services/app-web-bridge-service';
import { ExperimentService } from '@services/experiment';
import { ExploreService } from '@services/explore-service';
import { OnHoldUserResponseRequestBodyUserResponseEnum } from '@cureskin/api-client/src/server-api';
import { ApiClientConstant } from '@cureskin/api-client';
type TabStatusI = {
  HOME: boolean;
  CHAT: boolean;
  FOLLOWUP: boolean;
  REGIMEN: boolean;
  EXPLORE: boolean;
  SHOP: boolean;
  SUPPORT: boolean;
};
type TabNameI = {
  HOME: string,
  CHAT: string,
  FOLLOWUP: string,
  REGIMEN: string,
  EXPLORE: string,
  SHOP: string,
};

@Component({
  selector: 'user-view',
  templateUrl: './user-view.html',
  standalone: false,
})
export class UserViewComponent implements OnDestroy {
  tabs: TabNameI = {
    HOME: 'HOME',
    CHAT: 'CHAT',
    FOLLOWUP: 'FOLLOWUP',
    REGIMEN: 'REGIMEN',
    EXPLORE: 'EXPLORE',
    SHOP: 'SHOP',
  };
  userStatus: any = { PAID: false };
  isTabActive: TabStatusI = {} as TabStatusI;
  tabAnimation: TabNameI = <TabNameI>{};
  selectedIndex: number = -1;
  subscriptions: Array<Subscription> = [];
  isInternalUser: boolean = false;
  user: any;
  experiments: Array<any> = [];
  regimens: Array<any> = [];
  notification: any = {};
  homePage: boolean = false;
  isTabHasUpdates: Partial<TabStatusI> = {
    CHAT: false,
    FOLLOWUP: false,
    REGIMEN: false,
    EXPLORE: false,
    SUPPORT: false,
    SHOP: false,
  };
  showOnHoldPopup: boolean = false;
  onHoldRegimenClass: string;
  onHoldTicket: any;
  showFeelingBetterPopup: boolean = false;
  showNotFeelingBetterPopup: boolean = false;
  onHoldData: any;
  onHoldPopupResponse: any;
  userResponse: any;
  isTabUpdatesFetched: boolean;
  hideBottomNavBar: boolean = false;
  exploreSectionExperiment: boolean = false;
  showNotificationEnablePopUp: boolean = false;
  isNotificationPermissionEnabled: any;
  showShowExploreTab: boolean = false;
  shouldExecuteForUnpaidUser: boolean = false;
  isUnpaidUser: boolean = false;
  private readonly TAB_QUERY_PARAM = 'tab';
  private readonly HOME_TAB = 'home';
  private readonly exploreTabSubscription: Subscription;

  constructor(private conn: ConnectionService,
    private route: ActivatedRoute,
    private router: Router,
    private localStorage: LocalStorageService,
    private eventLogger: EventLoggerService,
    public appConfig: AppConfig,
    private readonly appBridge: AppWebBridgeService,
    private readonly experimentService: ExperimentService,
    private readonly zone: NgZone,
    private broadcastService: BroadcastService,
    private readonly exploreService: ExploreService
  ) {
    this.broadcastService.on('HOMEPAGE_FULLSCREEN_MODE_ON').subscribe((): void => {
      this.hideBottomNavBar = true;
    });
    this.broadcastService.on('HOMEPAGE_FULLSCREEN_MODE_OFF').subscribe((): void => {
      this.hideBottomNavBar = false;
    });

    // Subscribe to explore tab visibility changes
    this.exploreTabSubscription = this.exploreService.showExploreTab$.subscribe(
      (showTab: boolean) => {
        this.showShowExploreTab = showTab;
      }
    );
  }

  /**
   * Subscribes to query param change and animates the tab visibility.
   * Hides the active tab and brings in new tab to visibility.
   * Fetches for other tab updates every 30 seconds.
   */
  async ngOnInit(): Promise<any> {
    this.user = this.conn.getActingUser();
    this.experiments = await this.conn.findUserActiveExperiments();
    this.isInternalUser = this.conn.isInternalUser();
    this.findUserStatus();
    this.subscriptions.push(this.route.queryParams.subscribe(async ({ tab, username }: { tab: string, username: string }): Promise<any> => {
      let index;
      switch (tab) {
        case 'explore': {
          index = 3;
          break;
        }
        case 'regimen': {
          index = 2;
          break;
        }
        case 'shop': {
          index = 4;
          break;
        }
        case 'followups':
        case 'chat': {
          index = 1;
          break;
        }
        case 'home':
        default:
          index = 0;
      }
      if (this.selectedIndex === index) return;
      if (this.selectedIndex === -1) {
        this.tabAnimation[this.getTabNameByIndex(index)] = 'trans-xy-0';
      } else if (this.selectedIndex > index) {
        this.tabAnimation[this.getTabNameByIndex(this.selectedIndex)] = 'trans-x-hide';
        this.tabAnimation[this.getTabNameByIndex(index)] = 'trans-xy-0';
      } else if (this.selectedIndex < index) {
        this.tabAnimation[this.getTabNameByIndex(this.selectedIndex)] = 'trans-x-hide';
        this.tabAnimation[this.getTabNameByIndex(index)] = 'trans-xy-0';
      }
      this.isTabActive[this.getTabNameByIndex(index)] = true;
      if (this.selectedIndex >= 0) this.isTabActive[this.getTabNameByIndex(this.selectedIndex)] = false;
      this.selectedIndex = index;
      setTimeout((): Promise<void> => this.getOtherTabUpdates(), 500);
    }));
    await this.checkForOnHoldExp();
    this.subscriptions.push(interval(30 * 1000).subscribe((): boolean => this.isTabUpdatesFetched = false));

    // Call explore tab experiment handler with current user state
    this.exploreService.handleExploreTabExperiment(
      this.experiments,
      this.user?.get('orderState')
    );

    await this.experimentService.fetchActiveUserExperiment();
    await this.checkIfNotificationIsEnabled();
    this.subscriptions.push(
      this.broadcastService.on<string>('ACTIVITY_RESUME').subscribe((authInfo: string) => {
        this.zone.run(() => {
          (async () => {
            await this.checkIfNotificationIsEnabled();
          })().catch(err => {
            console.error('Error in ACTIVITY_RESUME handler:', err);
          });
        });
      })
    );
  }

  async checkForOnHoldExp(): Promise<any> {
    if (!this.isHomeTabActive()) {
      return;
    }
    const onHoldExperiment = this.experiments.find((experiment: any) => experiment.key === 'on_hold_fe');
    this.isUnpaidUser = this.user.get('orderState') !== ApiClientConstant.User.OrderState.DELIVERED;
    this.shouldExecuteForUnpaidUser = this.isUnpaidUser;
    if(onHoldExperiment && !this.shouldExecuteForUnpaidUser) {
      await this.checkOnHoldPopup();
    }
  }

  async checkOnHoldPopup(): Promise<void> {
    const onHoldPopup = await this.conn.showOnHoldPopup();
    const activeOnHold = Object.entries(onHoldPopup).find(
      ([_, value]) => value.showPopUp
    );

    if (activeOnHold) {
      const [key, value] = activeOnHold;
      this.onHoldData = value;
      this.onHoldRegimenClass = key;

      // will remove this after testing
      // this.onHoldTicket = await this.getTicketDetail(this.onHoldData.ticketId);
      // const discomfortAck: any = this.onHoldTicket.get('discomfortAcknowledgement');

      // if ([ApiClientConstant.SupportTicket.DiscomfortAcknowledgement.NOT_RESPONDED, ApiClientConstant.SupportTicket.DiscomfortAcknowledgement.STILL_FACING_ISSUE].includes(discomfortAck)) {
      //   this.showOnHoldPopup = false;
      // }
      // if(discomfortAck) {
      //   return;
      // }
      if(this.localStorage.getValue('showOnHoldPopup')) {
        await this.onHoldResponse(ApiClientConstant.SupportTicket.DiscomfortAcknowledgement.NOT_RESPONDED);
        return;
      }
      this.showOnHoldPopup = true;
      this.localStorage.setValue('showOnHoldPopup', true);
    }
  }

  async getTicketDetail(ticketId: string): Promise<Table.SupportTicket> {
    return await this.conn.findSupportTicketById(ticketId, { translate: true });
  }
  async onHoldResponse(response: OnHoldUserResponseRequestBodyUserResponseEnum): Promise<any> {
    this.onHoldPopupResponse = await this.conn.onHoldPopupUserAction(this.onHoldData.regimenObjectId, this.onHoldData.ticketId, response);
    if(response === ApiClientConstant.SupportTicket.DiscomfortAcknowledgement.STILL_FACING_ISSUE) {
      this.showNotFeelingBetterPopup = true;
    }
    if(response === ApiClientConstant.SupportTicket.DiscomfortAcknowledgement.BETTER) {
      this.showFeelingBetterPopup = true;
    }
    this.showOnHoldPopup = false;
    this.userResponse = response;
  }

  getTabNameByIndex(index: number): string {
    if (index === 0) return this.tabs.HOME;
    if (index === 1) return this.tabs.CHAT;
    if (index === 2) return this.tabs.REGIMEN;
    if (index === 4) return this.tabs.SHOP;
    return this.tabs.EXPLORE;
  }

  /**
   * Routes to specific tab by changing name of tab in query params.
   * @param {number} index - tab index that has to be routed to.
   * @returns {any}
   */
  onTabChange(index: number): any {
    this.findUserStatus();
    if (index === this.selectedIndex) return 0;
    let queryParams: any;
    switch (index) {
      case 3: {
        queryParams = { tab: 'explore' };
        return this.router.navigate(['/explore'], { queryParams });
      }
      case 4: {
        queryParams = { tab: 'shop' };
        return this.router.navigate(['/shop'], { queryParams });
      }
      case 2: {
        queryParams = { tab: 'regimen' };
        return this.router.navigate(['/regimen'], { queryParams });
      }
      case 0:
      default:
        queryParams = { tab: 'home' };
    }
    return this.router.navigate([], { queryParams, relativeTo: this.route });
  }

  /**
   * 1. Chat tab - Checks for new message in chat tab.
   * 2. Chat -> Followup tab - Checks for unread followup reports.
   * 3. Regimen tab - Checks for updates in regimen tab based of 'regimenUpdateVisited' flag in local storage.
   * 4. Explore tab - Checks for new feeds in explore tab.
   */
  async getOtherTabUpdates(): Promise<void> {
    if (!this.isHomeTabActive()) {
      return;
    }
    if (this.isTabUpdatesFetched) return;
    this.isTabUpdatesFetched = true;
    if (!this.user) this.user = await this.conn.getActingUser();

    let where: any = { UserId: this.user?.get('username') };

    const unReadReports = await this.conn.countFollowUpReports({ where: { user: this.user, read: { $exists: false } } });
    this.isTabHasUpdates.FOLLOWUP = !!unReadReports;

    this.isTabHasUpdates.REGIMEN = this.localStorage.getValue('regimenUpdateVisited', 'true') === 'false';

    const exploreTabLastVisitedAt = this.localStorage.getJsonValue('CureSkin/lastActiveTime', '{}').EXPLORE_TAB;
    where = {};
    if (exploreTabLastVisitedAt) where.$gt = { createdAt: exploreTabLastVisitedAt };
  }

  stopPropagation(event: any): void {
    event.stopPropagation();
  }

  cameraClick(): void {
    this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'home-camera' }));
    this.navigateTo('/user/instantCheckup');
  }

  shopClick(): void {
    this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'home-shop' }));
  }

  needHelpClick(): void {
    this.eventLogger.trackEvent('home-need-help-nav');
    this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'home-need-help-nav', pageName: this.route.snapshot.queryParams }));
    this.navigateTo('/support');
  }

  exploreClick(): void {
    this.eventLogger.cleverTapEvent('click-explore', JSON.stringify({ pageName: this.route.snapshot.queryParams }));
  }

  navigateTo(url: string): void {
    this.conn.navigateToURL(url);
  }

  async findUserStatus(): Promise<any> {
    if (this.user?.get('orderState') === 'DELIVERED') this.userStatus.PAID = true;
  }

  async setNewRegimenFlag(state: boolean): Promise<void> {
    this.findUserStatus();
    this.hideBottomNavBar = state;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription: Subscription): void => subscription.unsubscribe());
    this.subscriptions = [];
  }

  async checkIfNotificationIsEnabled(): Promise<void> {
    try {
      // Get permission status from app/device
      const deviceStatus = (await this.appBridge.requestNotificationStatus()) ?? false;
      this.isNotificationPermissionEnabled = deviceStatus;

      // Fetch current value from backend
      const backendStatus = await this.conn.showNotificationPermissionPopup();

      const backendAllowed = !!backendStatus?.notificationAllowedAt;

      // Compare and update if there's a mismatch
      if (deviceStatus !== backendAllowed) {
        await this.conn.setNotificationPopupResponse(deviceStatus);

        // Fetch again to verify update
        const updatedBackendStatus = await this.conn.showNotificationPermissionPopup();

        this.showNotificationEnablePopUp = updatedBackendStatus?.showNotificationPopUp;
      } else {
        this.showNotificationEnablePopUp = backendStatus?.showNotificationPopUp;
      }

    } catch (error) {
      console.error('Error in checking notification permission:', error);
      this.isNotificationPermissionEnabled = false;
      this.showNotificationEnablePopUp = false;
    }
  }


  openNativePermissionPopup(): void {
    this.appBridge.requestNotificationPermission();
    this.showNotificationEnablePopUp = false;
  }

  closeNotificationEnablePopup(): void {
    this.showNotificationEnablePopUp = false;
  }

  get isBottomNavExperimentActive(): boolean {
    return this.experimentService.isBottomNavConfigActive();
  }

  get isOnHoldPopupVisible(): boolean {
    return this.showOnHoldPopup || this.showNotFeelingBetterPopup || this.showFeelingBetterPopup;
  }

  private isHomeTabActive(): boolean {
    return this.route.snapshot.queryParamMap.get(this.TAB_QUERY_PARAM) === this.HOME_TAB;
  }

  async respondPermissionPopup(isEnable: boolean): Promise<void> {
    try {
      const eventName = isEnable ? 'enabled' : 'disabled';
      if (isEnable) {
        this.openNativePermissionPopup();
      } else {
        this.closeNotificationEnablePopup();
      }
      // send and save response in DB
      await this.conn.setNotificationPopupResponse(isEnable);
      this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: `notifications_${eventName}_popup` }));
    } catch (error) {
      console.error('Error in respondPermissionPopup:', error);
      this.broadcastService.broadcast('NOTIFY', { message: 'Failed to save notification preference' });
    }
  }
}
